<template>
  <div>
    <form class="flex flex-wrap">
      <div class="mb-3 w-full px-3" :class="isAdult ? 'lg:w-1/3' : 'lg:w-1/4'">
        <BaseFormInput
          v-model:value="patient.first_name"
          name="first_name"
          :label="$t('auth.first_name')"
          :errors="errors"
          :is-required="true"
        />
      </div>
      <div class="mb-3 w-full px-3" :class="isAdult ? 'lg:w-2/3' : 'lg:w-1/4'">
        <BaseFormInput
          v-model:value="patient.last_name"
          name="last_name"
          :label="$t('auth.last_name')"
          :errors="errors"
          :is-required="true"
        />
      </div>

      <div class="mb-3 w-full px-3 lg:w-1/2">
        <BaseFormInput
          v-if="!patient.isNoPesel"
          v-model:value="patient.pesel"
          name="pesel"
          :label="$t('auth.pesel')"
          :errors="errors"
          :is-required="true"
          type="number"
          class="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />

        <BaseDatePicker
          v-else
          v-model:value="patient.birth_date"
          name="birth_date"
          :label="$t('patients.enter_date_of_birth')"
          :any-dirty="v$.$anyDirty"
          :errors="errors"
          :is-required="true"
          :min="minBirthDate"
          :max="maxBirthDate"
        />

        <BaseCheckbox
          :id="`isNoPesel-${patient.id}`"
          name="pesel"
          theme="square"
          :checked="patient.isNoPesel"
          class="mb-5 leading-none"
          @change="(checked) => onUpdateIsNoPesel(checked)"
        >
          <span
            class="cursor-pointer text-sm font-medium leading-none text-darkestGray"
          >
            {{ $t('auth.checkboxes.no_pesel_number') }}
          </span>
        </BaseCheckbox>
      </div>

      <div v-if="isAdult" class="mb-3 w-full px-3 lg:w-1/2">
        <BasePhoneNumberInput
          name="phone_number"
          :phone-number="patient.phone_number"
          :country="patient.country"
          :errors="errors"
          :is-required="true"
          @update="onUpdatePhoneNumber($event)"
        />
      </div>

      <div class="w-full px-2 lg:w-1/2" v-if="patient.isNoPesel">
        <BaseSelect
          :model-value="patient.sex"
          :label="$t('auth.gender.title')"
          :options="sexOptions"
          :is-required="true"
          @update:model-value="(sex) => (patient.sex = sex)"
        />
      </div>

      <div class="mt-2 w-full px-3">
        <BaseLink v-if="!isCurrentUser" @click="onClickDeletePatient">
          <div class="flex items-center space-x-2.5">
            <img
              src="~/assets/icons/base/close-light.svg"
              alt="close-light-icon"
              loading="lazy"
            />
            <span> {{ $t('patients.remove_patient') }}</span>
          </div>
        </BaseLink>
      </div>
    </form>
  </div>
</template>

<script setup>
import useVuelidate from '@vuelidate/core'
import { and, or, required, requiredIf } from '@vuelidate/validators'
import { useAuthStore } from '~~/stores/auth'
import { useCartStore } from '~~/stores/cart'
import { useModalsStore } from '~~/stores/modals'

const emptyPatient = {
  age: '',
  birth_date: '',
  id: '',
  isNoPesel: false,
  first_name: '',
  last_name: '',
  pesel: '',
  sex: '',
}

const props = defineProps({
  initPatient: {
    type: [Object],
    required: true,
  },
})

const { t } = useI18n()
const {
  isValidPesel,
  hasAgeBetween,
  hasAgeGreaterThan,
  hasPeselAgeBetween,
  hasPeselAgeGreaterThan,
} = useValidationsRules()
const authStore = useAuthStore()
const modalsStore = useModalsStore()
const cartStore = useCartStore()

const sexOptions = reactive([
  { name: t('auth.gender.f'), value: 'f' },
  { name: t('auth.gender.m'), value: 'm' },
])

const patient = ref({ ...emptyPatient })

const isValidPhone = ref(false)
const errors = ref({})

const isAdult = computed(() => {
  console.log(patient.value.age)
  return patient.value.age === 'adult'
})

const wasAdult = computed(() => {
  return cartStore.getPatientById(props.initPatient.id).age === 'adult'
})

const isCurrentUser = computed(() => {
  return authStore.user?.id == patient.value.id
})

const minBirthDate = computed(() => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 18)
  return date
})

const maxBirthDate = computed(() => {
  const date = new Date()
  if (isAdult.value) {
    date.setFullYear(date.getFullYear() - 18)
  } else {
    date.setMonth(date.getMonth() - 3)
  }
  return date
})

const onUpdatePhoneNumber = (event) => {
  isValidPhone.value = event.isValid

  patient.value = {
    ...patient.value,
    country: event.countryCode,
    country_code: event.countryCallingCode,
    phone_number: event.nationalNumber,
  }
}

const onUpdateIsNoPesel = (checked) => {
  patient.value = {
    ...patient.value,
    isNoPesel: checked,
    pesel: checked ? null : patient.value.pesel,
  }
}

const onClickDeletePatient = () => {
  modalsStore.close('editPatientModal')
  cartStore.deletePatient(props.initPatient.id)
}

onMounted(() => {
  patient.value = { ...props.initPatient }

  if (props.initPatient.age === 'adult') {
    isValidPhone.value = true
  }
})

const isDistinctPeselInPatients = (value) => {
  let quantity = 0

  quantity += cartStore.patients.filter(
    (patient) => patient.pesel == value
  ).length
  quantity += cartStore.tmpPatients.filter(
    (patient) => patient.pesel == value
  ).length

  return quantity <= 1
}

const rules = computed(() => ({
  first_name: { required },
  last_name: { required },
  pesel: {
    required: requiredIf(() => !patient.value.isNoPesel),
    invalid: or(() => patient.value.isNoPesel, isValidPesel),
    invalid_date: or(
      () => patient.value.isNoPesel,
      and(
        () => patient.value.age === 'kid',
        (value) => hasPeselAgeBetween(value, 3, 216)
      ),
      and(
        () => patient.value.age === 'adult',
        (value) => hasPeselAgeGreaterThan(value, 216)
      )
    ),
    distinct: or(
      () => patient.value.isNoPesel,
      (value) => isDistinctPeselInPatients(value)
    ),
  },
  sex: { required: requiredIf(() => patient.value.isNoPesel) },
  birth_date: {
    required: requiredIf(() => patient.value.isNoPesel),
    invalid_date: or(
      () => !patient.value.isNoPesel,
      and(
        () => patient.value.age === 'kid',
        (value) => hasAgeBetween(value, 3, 216)
      ),
      and(
        () => patient.value.age === 'adult',
        (value) => hasAgeGreaterThan(value, 216)
      )
    ),
  },
}))

const v$ = useVuelidate(rules, patient)

const validate = async () => {
  errors.value = {}
  let isValid = await v$.value.$validate()

  if (!isValid) {
    v$.value.$errors.forEach((error) => {
      errors.value = {
        ...errors.value,
        [error.$property]: `error.${error.$property}.${error.$validator}`,
      }
    })
  } else {
    errors.value = {}
  }

  if (isAdult.value && !isValidPhone.value) {
    errors.value = {
      ...errors.value,
      phone_number: 'error.national_phone_number.required',
    }
    isValid = false
  }

  return isValid
}

const submit = async () => {
  const isValid = await validate()

  if (isValid) {
    cartStore.updatePatient(props.initPatient.id, patient.value)
  }

  return isValid
}

defineExpose({ submit })
</script>
